import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AddOnsHiddenReason } from '@components/add-ons-section';
import { SALES_START_TYPE_AFTER_OFFER, type SalesWindowContext } from '@components/admission-sales-dates';
import { CHANNEL_QUOTA_PER_CHANNEL } from '@components/distribution-section';
import { shouldPhoneBeRequiredForSiae } from '@components/forms/utils/format-booking-formFields-from-backend';
import { checkIsSiaeOfferInReview } from '@components/forms/utils/siae-status';
import { validateTeamChannels, type ValidationError } from '@components/team-channel-section';
import { type SupportedLanguages } from '@constants/constants.type';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { type Addon } from '@domain/addon';
import {
  type Admission,
  ADMISSION_STATUS_HIDDEN_BY_TRIGGER,
  ADMISSION_STATUS_IN_REVIEW,
  ADMISSION_STATUS_ON_SALE,
  ADMISSION_STATUS_SALES_CLOSED,
  ADMISSION_STATUS_SOLD_OUT,
  ADMISSION_TYPE_TICKET,
  type AdmissionType,
  type BottleService,
  type GuestList,
  type Status,
  type Ticket,
} from '@domain/admission';
import { type ActiveChannelRequest, type EventChannel } from '@domain/channel';
import { type Event, type EventSettings } from '@domain/event';
import { type EventTeam } from '@domain/eventTeam';
import { type FormFieldPreset } from '@domain/form-field-presets';
import { useGetAddons } from '@hooks/addons/use-get-addons';
import { type CreateAdmissionRequest, type OfferTeam } from '@hooks/admissions/type';
import { useGetOfferTeams } from '@hooks/admissions/use-get-offer-teams';
import { useSaveOfferTeamMaxQuantity } from '@hooks/admissions/use-save-offer-team-max-quantity';
import { useGetCompanyCheckoutFormPresets } from '@hooks/company/use-get-company-checkout-form-presets';
import {
  getTicketsLeftThresholdByType,
  useGetCompanyTicketsLeftThreshold,
} from '@hooks/company/use-get-company-tickets-left-threshold';
import { useGetCompanyVatRates } from '@hooks/company/use-get-company-vat-rates';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetFloorplan } from '@hooks/floorplan/use-get-floorplan';
import { type GalleryImagesController, useGalleryImages } from '@hooks/gallery/use-gallery-images';
import { useGetPresetActiveChannels } from '@hooks/presets/admissions/use-get-admission-preset-channels';
import { useGetEventTeamChannels } from '@hooks/team-channel/use-get-event-team-channels';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { useGetFormFieldsPresets } from '@hooks/use-form-fields-presets/use-form-fields-presets';
import { useToast } from '@hooks/use-toast/use-toast';
import { admissionCapacityAchieved } from '@utils/admission-capacity-achieved/admission-capacity-achieved';
import { filterTreeToSelected } from '@utils/channel-tree';
import { isEventInThePast as checkIsEventInThePast } from '@utils/is-event-in-the-past';
import moment from 'moment-timezone';

import { buildFormFieldsValue } from './mappers/form-fields';
import { seedChannelLimit, toAdmissionFormValue } from './mappers/from-backend';
import {
  applySalesTimeGranularity,
  applySettingsGranularity,
  diffAdmissionRequest,
  diffTeamCaps,
  isChannelGroupDirty,
  toCreateAdmissionRequest,
} from './mappers/to-backend';
import { resolveInitialVatRate } from './mappers/vat-rate';
import {
  type AdmissionFormProps,
  type AdmissionFormType,
  type AdmissionFormValue,
  type SectionKey,
} from './admission-form.type';
import { ADMISSION_FORM_FLOW_PRESET, ADMISSION_FORM_MODE_EDIT, ADMISSION_FORM_TYPE_PASS } from './constants';
import { hasIncompleteNumericField, hasUnnamedCustomField } from './offer-form-common';
import { getFormTitle, getSubmitLabel } from './section-header';
import {
  type AdmissionFormErrors,
  isAdmissionFormValid,
  selectVisibleErrors,
  validateAdmissionForm,
  validateChannelLimit,
} from './validation';
import { getVisibleSections } from './visibility';

export interface UseAdmissionFormResult {
  methods: UseFormReturn<AdmissionFormValue>;
  title: string;
  submitLabel: string;
  visibleSections: SectionKey[];
  openSection: SectionKey | null;
  toggleSection: (key: SectionKey) => void;
  errors: AdmissionFormErrors;
  isSaveDisabled: boolean;
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  onSave: () => void;
  onClose: () => void;
  handleStatusChange: (newStatus: Status, apply: (status: Status) => void) => void;
  currency: string;
  mainLanguage: SupportedLanguages;
  timezone: string;
  availableAddOns: Addon[];
  addOnsLoading: boolean;
  onOpenPresetsModal: () => void;
  isAddOnsPresetsModalOpen: boolean;
  closeAddOnsPresetsModal: () => void;
  onAddOnsPresetsSaved: (createdAddonIds: string[]) => void;
  eventId: string;
  companyClubId: string;
  addOnsHiddenReason?: AddOnsHiddenReason;
  isAddOnsSectionHidden: boolean;
  formFieldPresets: FormFieldPreset[];
  isPhoneLockedRequired: boolean;
  galleryController: GalleryImagesController;
  floorplanImageUrl?: string;
  salesWindowContext: SalesWindowContext;
  minValidFromDate: moment.Moment;
  maxEndDate: moment.Moment;
  channelErrors?: ValidationError[];
  channelLimitError?: string;
  channelLimitLoading: boolean;
  accessCodeError: boolean;
  isEventInThePast: boolean;
  isPricingLocked: boolean;
  isDownpaymentLocked: boolean;
  isFormFieldsLocked: boolean;
  isEditingLocked: boolean;
  statusLock: StatusLock;
  showStatusControl: boolean;
  onOpenStartAfter?: () => void;
}

interface StatusLock {
  blocked: boolean;
  reasonKey: string;
}

const offerHasSoldItems = (offer: Admission | null | undefined): boolean => (offer ? offer.soldItems > 0 : false);

export const filterTreeToEnabledChannels = (
  tree: EventTeam | undefined,
  eventChannels: EventChannel[] | undefined,
  isEventChannelsPending: boolean
): EventTeam | undefined => {
  if (!tree) return undefined;
  if (!eventChannels) return isEventChannelsPending ? undefined : tree;
  const enabledChannelIds = eventChannels.filter((channel) => channel.isEnabled).map((channel) => channel.channel.id);
  return filterTreeToSelected(tree, enabledChannelIds);
};

const computeStatusLock = (
  offer: Admission | null | undefined,
  admissionType: AdmissionFormType,
  event: Event
): StatusLock => {
  const ticketCapacityAchieved =
    admissionType === ADMISSION_TYPE_TICKET &&
    admissionCapacityAchieved({ maxCapacity: event.ticketMaxCapacity, soldItems: event.soldItems.ticket });
  if (ticketCapacityAchieved) return { blocked: true, reasonKey: 'offers:capacityAchievedBlockedChangeStatusMessage' };
  if (offer?.status === ADMISSION_STATUS_HIDDEN_BY_TRIGGER)
    return { blocked: true, reasonKey: 'offers:hiddenByTriggerOfferBlockedChangeStatusMessage' };
  if (offer?.status === ADMISSION_STATUS_IN_REVIEW)
    return { blocked: true, reasonKey: 'offers:inReviewOfferBlockedChangeStatusMessage' };
  return { blocked: false, reasonKey: '' };
};

const computeIsPricingLocked = (
  offer: Admission | null | undefined,
  settings: EventSettings,
  isDuplicatedAdmission: boolean
): boolean =>
  offerHasSoldItems(offer) || (!isDuplicatedAdmission && Boolean(checkIsSiaeOfferInReview(offer, settings)));

const ACCESS_CODE_PATTERN = /^\w*$/;

const isClosingInPast = (endDate: moment.Moment, endTime: string): boolean => {
  const [hours, minutes] = endTime.split(':');
  return endDate.clone().hours(Number.parseInt(hours, 10)).minutes(Number.parseInt(minutes, 10)).isBefore(moment());
};

const isFormSaveDisabled = ({
  isEventInThePast,
  isSeedPending,
  allErrors,
  channelErrors,
  channelLimitError,
  accessCodeError,
  formFields,
}: {
  isEventInThePast: boolean;
  isSeedPending: boolean;
  allErrors: AdmissionFormErrors;
  channelErrors?: ValidationError[];
  channelLimitError?: string;
  accessCodeError: boolean;
  formFields: AdmissionFormValue['formFields'];
}): boolean =>
  isEventInThePast ||
  isSeedPending ||
  !isAdmissionFormValid(allErrors) ||
  (channelErrors?.length ?? 0) > 0 ||
  !!channelLimitError ||
  accessCodeError ||
  hasIncompleteNumericField(formFields.mainHolder.customFields) ||
  hasUnnamedCustomField(formFields.mainHolder.customFields) ||
  (formFields.isNominative &&
    (hasIncompleteNumericField(formFields.ticketHolder.customFields) ||
      hasUnnamedCustomField(formFields.ticketHolder.customFields)));

const usePortugueseNifPrecheck = (
  event: Event,
  offer: Admission | null | undefined,
  isCreatedFromPreset: boolean
): { shouldPrecheckTaxIdPt: boolean; isCheckoutPresetsPending: boolean } => {
  const { companyState } = useContext(CompanyClubContext);
  const isCreation = !offer || isCreatedFromPreset;
  const isEventInPortugal = event.club.city.country.isoCode === 'PT';
  const isPortugueseNifActive = isEventInPortugal;
  const isFetchEnabled = !!companyState.id && isPortugueseNifActive && isCreation;
  const { data: checkoutFormPresets, isLoading } = useGetCompanyCheckoutFormPresets({
    companyId: companyState.id,
    options: { enabled: isFetchEnabled },
  });

  return {
    shouldPrecheckTaxIdPt: isCreation && isEventInPortugal && !!checkoutFormPresets?.isTaxIdPtEnabledByDefault,
    isCheckoutPresetsPending: isFetchEnabled && isLoading,
  };
};

const useOfferChannelLimits = (
  offer: Admission | null | undefined,
  isCreatedFromPreset: boolean,
  skipOfferFetch: boolean
): { offerTeams: OfferTeam[] | undefined; activeChannels: ActiveChannelRequest | undefined; isPending: boolean } => {
  const needsOfferFetch = !!offer?.id && !skipOfferFetch;
  const { data: offerTeams, isFetching: isOfferTeamsFetching } = useGetOfferTeams({
    offerId: offer?.id,
    enabled: needsOfferFetch,
  });
  const { data: offerActiveChannels, isFetching: isActiveChannelsFetching } = useGetActiveChannels({
    offerId: offer?.id,
    enabled: needsOfferFetch,
  });
  // A preset-seeded create skips the offer-channels fetch (no real offer), so its channel selection
  // comes from the preset's own /channels endpoint — the list payload doesn't carry it.
  const needsPresetFetch = isCreatedFromPreset && !!offer?.id;
  const { data: presetActiveChannels, isFetching: isPresetChannelsFetching } = useGetPresetActiveChannels({
    presetId: offer?.id,
    enabled: needsPresetFetch,
  });
  // Treat an in-flight refetch as pending too: on reopen react-query serves the stale cache while it refetches,
  // and the one-shot seed would otherwise snapshot that stale value before the fresh data lands.
  const isOfferFetchPending =
    needsOfferFetch && (!offerTeams || !offerActiveChannels || isOfferTeamsFetching || isActiveChannelsFetching);
  const isPresetFetchPending = needsPresetFetch && (!presetActiveChannels || isPresetChannelsFetching);
  return {
    offerTeams,
    activeChannels: isCreatedFromPreset ? presetActiveChannels : offerActiveChannels,
    isPending: isOfferFetchPending || isPresetFetchPending,
  };
};

const isAdmissionSeedPending = (params: {
  formFieldPresetsData: unknown;
  isPreset: boolean;
  eventTeamChannels: EventTeam | undefined;
  isOfferChannelLimitsPending: boolean;
  isCheckoutPresetsPending: boolean;
}): boolean =>
  !params.formFieldPresetsData ||
  (!params.isPreset && !params.eventTeamChannels) ||
  params.isOfferChannelLimitsPending ||
  params.isCheckoutPresetsPending;

interface EditDiffParams {
  fullPayload: CreateAdmissionRequest;
  value: AdmissionFormValue;
  defaultValues: AdmissionFormValue;
  eventTeamChannels: EventTeam | undefined;
  offerTeams: OfferTeam[] | undefined;
  activeChannels: ActiveChannelRequest | undefined;
  offer: Admission | null | undefined;
  initialData: AdmissionFormValue | undefined;
  bookingPresets: FormFieldPreset[];
  isPhoneLockedRequired: boolean;
  buildRequest: (formValue: AdmissionFormValue, triggerId: string | null) => CreateAdmissionRequest;
}

const buildEditDiffPayload = ({
  fullPayload,
  value,
  defaultValues,
  eventTeamChannels,
  offerTeams,
  activeChannels,
  offer,
  initialData,
  bookingPresets,
  isPhoneLockedRequired,
  buildRequest,
}: EditDiffParams): { payload: CreateAdmissionRequest; dirtyTeamCaps: ReturnType<typeof diffTeamCaps> } => {
  const baselineChannelLimit =
    defaultValues.distribution.channelLimit ??
    (eventTeamChannels ? seedChannelLimit(eventTeamChannels, offer, offerTeams, activeChannels) : undefined);
  const baselineValue: AdmissionFormValue = {
    ...defaultValues,
    distribution: { ...defaultValues.distribution, channelLimit: baselineChannelLimit },
    formFields: initialData
      ? defaultValues.formFields
      : buildFormFieldsValue({ offer, presets: bookingPresets, isPhoneLockedRequired }),
  };
  const isStatusDirty = value.status !== defaultValues.status;
  const baselinePayload = buildRequest(baselineValue, null);
  const isChannelsDirty = isChannelGroupDirty(fullPayload, baselinePayload);
  const alwaysKeep: (keyof CreateAdmissionRequest)[] = [];
  if (isStatusDirty) alwaysKeep.push('salesStatus');
  if (isChannelsDirty) alwaysKeep.push('channels');
  const payload = diffAdmissionRequest(fullPayload, baselinePayload, alwaysKeep);
  applySalesTimeGranularity(payload, baselinePayload, value.basicInfo.salesWindow?.startType);
  applySettingsGranularity(payload, fullPayload.settings, baselinePayload.settings, isStatusDirty, isChannelsDirty);
  return { payload, dirtyTeamCaps: diffTeamCaps(value.distribution.channelLimit, baselineChannelLimit) };
};

export const useAdmissionForm = ({
  flow,
  admissionType,
  mode,
  event,
  offer,
  initialData,
  onSave: onSaveProp,
  onClose: onCloseProp,
  onDirtyChange,
  galleryController: galleryControllerProp,
  isDuplicatedAdmission = false,
  isCreatedFromPreset = false,
}: AdmissionFormProps): UseAdmissionFormResult => {
  const { t, i18n } = useTranslation('offers');
  const toast = useToast();
  const { clubState, companyState, companyClubState, isExtraGuestsForBottleServiceActive } =
    useContext(CompanyClubContext);
  const { userState } = useContext(UserContext);
  const { isTicketsLeftThresholdEnabled } = useContext(FeatureFlagContext);

  const mainLanguage = (i18n.language?.split('-')[0] ?? 'en') as SupportedLanguages;
  const timezone = clubState.city.timezone;
  const isClubCountryItaly = clubState.city.country.isoCode === 'ITA';
  const isSuperAdmin = userState?.isSuperAdmin ?? false;
  const canExcludeSiaeOffers = event.companyClub.company?.isAllowedToExcludeSiaeOffers ?? false;
  const hasItalyRegulationContext = isClubCountryItaly;
  const isPreset = flow === ADMISSION_FORM_FLOW_PRESET;
  const isEventInThePast = checkIsEventInThePast(event);

  const minValidFromDate = useMemo(() => moment.unix(event.startingTime).tz(timezone), [event.startingTime, timezone]);
  const maxEndDate = useMemo(() => moment.unix(event.endingTime).tz(timezone), [event.endingTime, timezone]);

  const visibleSections = useMemo(
    () =>
      getVisibleSections({
        flow,
        type: admissionType,
        isClubCountryItaly,
        hasItalyRegulationContext,
        isSuperAdmin,
        canExcludeSiaeOffers,
      }),
    [flow, admissionType, isClubCountryItaly, hasItalyRegulationContext, isSuperAdmin, canExcludeSiaeOffers]
  );

  const defaultValues = useMemo<AdmissionFormValue>(
    () =>
      initialData ??
      toAdmissionFormValue({
        flow,
        type: admissionType,
        offer,
        mainLanguage,
        timezone,
        now: moment.tz(timezone),
        eventStartingTime: event.startingTime,
        eventEndingTime: event.endingTime,
        isSuperAdmin,
        canExcludeSiaeOffers,
        hasItalyRegulationContext,
        isTicketsLeftThresholdEnabled,
        isExtraGuestsForBottleServiceEnabled: isExtraGuestsForBottleServiceActive,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const methods = useForm<AdmissionFormValue>({ defaultValues, mode: 'onChange' });
  const hasUnsavedChanges = Object.keys(methods.formState.dirtyFields).length > 0;

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const { data: addOnsData, isLoading: addOnsLoading } = useGetAddons({
    eventId: event.id,
    enabled: !isPreset && admissionType !== ADMISSION_FORM_TYPE_PASS,
  });
  const { data: companyVatRates } = useGetCompanyVatRates({
    companyId: companyState.id,
    options: { enabled: !!companyState.id && !isClubCountryItaly },
  });
  const { data: companyTicketsLeftThreshold } = useGetCompanyTicketsLeftThreshold({
    companyId: companyState.id,
    options: { enabled: !!companyState.id },
  });
  const { data: formFieldPresetsData } = useGetFormFieldsPresets();
  const { shouldPrecheckTaxIdPt, isCheckoutPresetsPending } = usePortugueseNifPrecheck(
    event,
    offer,
    isCreatedFromPreset
  );
  const hasEventChannelContext = !isPreset && !!event.id;
  const { data: rawEventTeamChannels } = useGetEventTeamChannels({
    eventId: event.id,
    options: { enabled: hasEventChannelContext },
  });
  const { data: eventChannels, isPending: isEventChannelsPending } = useGetChannels({
    eventId: event.id,
    enabled: hasEventChannelContext,
  });
  const eventTeamChannels = useMemo(
    () => filterTreeToEnabledChannels(rawEventTeamChannels, eventChannels, isEventChannelsPending),
    [rawEventTeamChannels, eventChannels, isEventChannelsPending]
  );
  const {
    offerTeams,
    activeChannels,
    isPending: isOfferChannelLimitsPending,
  } = useOfferChannelLimits(offer, isCreatedFromPreset, isPreset || isCreatedFromPreset);
  const { mutate: saveOfferTeamMaxQuantity } = useSaveOfferTeamMaxQuantity();
  const { data: floorplan } = useGetFloorplan({ floorplanId: event.floorplanId });

  const bookingPresets = useMemo(() => formFieldPresetsData?.bookingFormFieldPresets ?? [], [formFieldPresetsData]);
  const offerForSiaeLocks = isCreatedFromPreset ? null : offer;
  const isPhoneLockedRequired = shouldPhoneBeRequiredForSiae(event, offerForSiaeLocks, companyState, admissionType);
  const isPricingLocked = computeIsPricingLocked(offerForSiaeLocks, event.settings, isDuplicatedAdmission);
  const isDownpaymentLocked = offerHasSoldItems(offer);
  const isFormFieldsLocked = offerHasSoldItems(offer);
  const statusLock = computeStatusLock(offer, admissionType, event);

  const internalGalleryController = useGalleryImages({
    entityId: offer?.id ?? null,
    initialImages: offer?.images ?? [],
    uploadImage: () => Promise.reject(new Error('Admission gallery persistence is wired in the drawer (B2B-1700)')),
    deleteImage: () => Promise.resolve(),
    reorderImages: () => Promise.resolve(),
  });
  const galleryController = galleryControllerProp ?? internalGalleryController;

  useEffect(() => {
    if ((offer && !isCreatedFromPreset) || initialData || !companyVatRates) return;
    const prefilled = resolveInitialVatRate({ companyVatRates, type: admissionType, offer, isCreatedFromPreset });
    methods.setValue('vatRate.vatRate', prefilled, { shouldDirty: false });
  }, [companyVatRates, offer, initialData, admissionType, methods, isCreatedFromPreset]);

  useEffect(() => {
    if (!eventTeamChannels || isOfferChannelLimitsPending || methods.getValues('distribution.channelLimit')) {
      return;
    }
    methods.setValue(
      'distribution.channelLimit',
      seedChannelLimit(eventTeamChannels, offer, offerTeams, activeChannels),
      {
        shouldDirty: false,
      }
    );
  }, [eventTeamChannels, offerTeams, activeChannels, isOfferChannelLimitsPending, offer, methods]);

  const formFieldsSeededRef = useRef(false);
  useEffect(() => {
    if (formFieldsSeededRef.current || initialData || !formFieldPresetsData || isCheckoutPresetsPending) return;
    formFieldsSeededRef.current = true;
    const seeded = buildFormFieldsValue({
      offer,
      presets: bookingPresets,
      shouldPrecheckTaxIdPt,
      isPhoneLockedRequired,
    });
    methods.setValue(
      'formFields',
      isCreatedFromPreset
        ? {
            ...seeded,
            mainHolder: {
              ...seeded.mainHolder,
              customFields: seeded.mainHolder.customFields.map((field) => ({ ...field, backendId: undefined })),
            },
            ticketHolder: {
              ...seeded.ticketHolder,
              customFields: seeded.ticketHolder.customFields.map((field) => ({ ...field, backendId: undefined })),
            },
          }
        : seeded,
      { shouldDirty: false }
    );
  }, [
    formFieldPresetsData,
    offer,
    initialData,
    bookingPresets,
    methods,
    isCheckoutPresetsPending,
    isPhoneLockedRequired,
    shouldPrecheckTaxIdPt,
    isCreatedFromPreset,
  ]);

  const [openSection, setOpenSection] = useState<SectionKey | null>(visibleSections[0] ?? null);
  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key));
  }, []);

  const [selectedOfferTrigger, setSelectedOfferTrigger] = useState<BottleService | GuestList | Ticket | null>(null);

  const watchedStartType = methods.watch('basicInfo.salesWindow.startType');
  const lastNonAfterOfferStartTypeRef = useRef(watchedStartType);
  useEffect(() => {
    if (watchedStartType && watchedStartType !== SALES_START_TYPE_AFTER_OFFER) {
      lastNonAfterOfferStartTypeRef.current = watchedStartType;
    }
  }, [watchedStartType]);

  const handleTriggerOfferCancel = useCallback(() => {
    const basicInfo = methods.getValues('basicInfo');
    const salesWindow = basicInfo.salesWindow;
    if (!salesWindow) return;
    methods.setValue(
      'basicInfo',
      {
        ...basicInfo,
        salesWindow: { ...salesWindow, startType: lastNonAfterOfferStartTypeRef.current ?? salesWindow.startType },
      },
      { shouldDirty: true }
    );
    setSelectedOfferTrigger(null);
  }, [methods]);

  const salesWindowContext = useMemo<SalesWindowContext>(
    () => ({
      now: moment.tz(timezone),
      timezone,
      locale: mainLanguage,
      eventId: event.id,
      eventStartingTime: event.startingTime,
      eventEndingTime: event.endingTime,
      maxEndDate: moment.unix(event.endingTime).tz(timezone),
      eventIsInThePast: isEventInThePast,
      offerType: admissionType as AdmissionType,
      offerId: isCreatedFromPreset ? undefined : offer?.id,
      selectedOfferTrigger,
      setSelectedOfferTrigger,
      onTriggerOfferCancel: handleTriggerOfferCancel,
    }),
    [
      timezone,
      mainLanguage,
      event.id,
      event.startingTime,
      event.endingTime,
      isEventInThePast,
      admissionType,
      offer?.id,
      isCreatedFromPreset,
      selectedOfferTrigger,
      handleTriggerOfferCancel,
    ]
  );

  const watchedValues = methods.watch();
  const allErrors = validateAdmissionForm({
    value: watchedValues,
    type: admissionType,
    mainLanguage,
    eventStartingTime: event.startingTime,
    eventEndingTime: event.endingTime,
    timezone,
    t,
  });
  const errors = selectVisibleErrors(allErrors, watchedValues, defaultValues);

  const channelLimit = watchedValues.distribution.channelLimit;
  const channelQuantity = watchedValues.basicInfo.quantity ? Number(watchedValues.basicInfo.quantity) : null;
  const channelErrors: ValidationError[] | undefined =
    channelLimit?.enabled && channelLimit.quotaMode === CHANNEL_QUOTA_PER_CHANNEL
      ? validateTeamChannels(
          filterTreeToSelected(channelLimit.channels, channelLimit.selectedChannelIds),
          channelQuantity
        )
      : undefined;
  const channelLimitError = validateChannelLimit(channelLimit, channelQuantity, t);

  const accessCode = watchedValues.distribution.accessCode;
  const accessCodeError = !!accessCode?.enabled && accessCode.code !== '' && !ACCESS_CODE_PATTERN.test(accessCode.code);

  const isSeedPending = isAdmissionSeedPending({
    formFieldPresetsData,
    isPreset,
    eventTeamChannels,
    isOfferChannelLimitsPending,
    isCheckoutPresetsPending,
  });

  const isSaveDisabled = isFormSaveDisabled({
    isEventInThePast,
    isSeedPending,
    allErrors,
    channelErrors,
    channelLimitError,
    accessCodeError,
    formFields: watchedValues.formFields,
  });

  const onSave = useCallback(() => {
    if (isSaveDisabled) return;
    const value = methods.getValues();
    if (
      !isAdmissionFormValid(
        validateAdmissionForm({
          value,
          type: admissionType,
          mainLanguage,
          eventStartingTime: event.startingTime,
          eventEndingTime: event.endingTime,
          timezone,
          t,
        })
      )
    )
      return;

    if (admissionType === ADMISSION_FORM_TYPE_PASS) return;

    const buildRequest = (formValue: AdmissionFormValue, triggerId: string | null): CreateAdmissionRequest =>
      toCreateAdmissionRequest({
        value: formValue,
        type: admissionType,
        currency: event.currency,
        mainLanguage,
        eventId: event.id,
        eventStartingTime: event.startingTime,
        eventEndingTime: event.endingTime,
        timezone,
        offer: isCreatedFromPreset ? null : offer,
        offerPresetId: isCreatedFromPreset ? offer?.id : undefined,
        defaultTicketsLeftThreshold: getTicketsLeftThresholdByType(companyTicketsLeftThreshold, admissionType),
        formFieldPresets: bookingPresets,
        selectedOfferTriggerId: triggerId,
      });

    const fullPayload: CreateAdmissionRequest = buildRequest(value, selectedOfferTrigger?.id ?? null);
    let payload: CreateAdmissionRequest = fullPayload;

    if (mode === ADMISSION_FORM_MODE_EDIT && offer && !isDuplicatedAdmission) {
      const { payload: editPayload, dirtyTeamCaps } = buildEditDiffPayload({
        fullPayload,
        value,
        defaultValues,
        eventTeamChannels,
        offerTeams,
        activeChannels,
        offer,
        initialData,
        bookingPresets,
        isPhoneLockedRequired,
        buildRequest,
      });
      payload = editPayload;
      dirtyTeamCaps.forEach(({ teamId, maxQuantity }) =>
        saveOfferTeamMaxQuantity({ offerId: offer.id, teamId, maxQuantity })
      );
    }

    void onSaveProp(payload);
  }, [
    isSaveDisabled,
    methods,
    mainLanguage,
    t,
    admissionType,
    mode,
    event,
    timezone,
    offer,
    initialData,
    isDuplicatedAdmission,
    isCreatedFromPreset,
    isPhoneLockedRequired,
    defaultValues,
    eventTeamChannels,
    offerTeams,
    activeChannels,
    saveOfferTeamMaxQuantity,
    onSaveProp,
    companyTicketsLeftThreshold,
    bookingPresets,
    selectedOfferTrigger,
  ]);

  const handleStatusChange = useCallback(
    (newStatus: Status, apply: (status: Status) => void) => {
      if (!offer) {
        apply(newStatus);
        return;
      }
      const prevStatus = offer.status;

      if (prevStatus === ADMISSION_STATUS_SOLD_OUT && newStatus === ADMISSION_STATUS_ON_SALE) {
        const quantity = Number(methods.getValues('basicInfo.quantity'));
        if (offer.quantity !== null && offer.soldItems >= quantity) {
          toast.push({
            title: t('offers:admissionChangeStatusNotEnoughQuantityTitle'),
            text: t('offers:admissionChangeStatusNotEnoughQuantityText'),
            type: 'error',
            disableTimeOut: true,
          });
          return;
        }
      }

      if (prevStatus === ADMISSION_STATUS_SALES_CLOSED && newStatus === ADMISSION_STATUS_ON_SALE) {
        const salesWindow = methods.getValues('basicInfo.salesWindow');
        if (salesWindow?.endDate && isClosingInPast(salesWindow.endDate, salesWindow.endTime)) {
          toast.push({
            title: t('offers:admissionChangeStatusFutureDateTitle'),
            text: t('offers:admissionChangeStatusFutureDateText'),
            type: 'error',
            disableTimeOut: true,
          });
          return;
        }
      }

      apply(newStatus);
    },
    [offer, methods, t, toast]
  );

  const isAddOnsSectionHidden =
    Number.parseFloat(watchedValues.basicInfo.price) === 0 && watchedValues.addOns.selectedAddOnIds.length === 0;

  const addOnsHiddenReason = useMemo<AddOnsHiddenReason | undefined>(() => {
    if (offer?.settings.italyRegulation?.allows18App) {
      return {
        title: t('addons:addonNotAvailableWithCartaCulturaTitle'),
        description: t('addons:addonNotAvailableWithCartaCulturaDescription'),
      };
    }
    return undefined;
  }, [offer, t]);

  const [isAddOnsPresetsModalOpen, setIsAddOnsPresetsModalOpen] = useState(false);

  const handleAddOnsPresetsSaved = useCallback(
    (createdAddonIds: string[]) => {
      const currentAddOns = methods.getValues('addOns');
      methods.setValue(
        'addOns',
        {
          ...currentAddOns,
          selectedAddOnIds: Array.from(new Set([...currentAddOns.selectedAddOnIds, ...createdAddonIds])),
        },
        { shouldDirty: true }
      );
    },
    [methods]
  );

  return {
    methods,
    title: getFormTitle(t, mode, flow, admissionType),
    submitLabel: getSubmitLabel(t, mode),
    isSaveDisabled,
    visibleSections,
    openSection,
    toggleSection,
    errors,
    hasUnsavedChanges,
    isLoading: addOnsLoading,
    onSave,
    onClose: onCloseProp,
    handleStatusChange,
    currency: event.currency,
    mainLanguage,
    timezone,
    availableAddOns: addOnsData ?? [],
    addOnsLoading,
    onOpenPresetsModal: () => setIsAddOnsPresetsModalOpen(true),
    isAddOnsPresetsModalOpen,
    closeAddOnsPresetsModal: () => setIsAddOnsPresetsModalOpen(false),
    onAddOnsPresetsSaved: handleAddOnsPresetsSaved,
    eventId: event.id,
    companyClubId: companyClubState.id,
    addOnsHiddenReason,
    isAddOnsSectionHidden,
    formFieldPresets: bookingPresets,
    isPhoneLockedRequired,
    galleryController,
    floorplanImageUrl: floorplan?.imageUrl,
    salesWindowContext,
    minValidFromDate,
    maxEndDate,
    channelErrors,
    channelLimitError,
    channelLimitLoading: isSeedPending,
    accessCodeError,
    isEventInThePast,
    isPricingLocked,
    isDownpaymentLocked,
    isFormFieldsLocked,
    isEditingLocked: false,
    statusLock,
    showStatusControl: true,
  };
};
